import { transport } from "@/src/config/apiConfig.js";
import { codebaseTransport } from "@/src/config/codebaseTransport.js";
import { codeActionsTransport } from "@/src/config/codeActionsTransport.js";
import { codeReviewerTransport } from "@/src/config/codeReviewerTransport.js";
import { coderTransport } from "@/src/config/coderTransport.js";
import { reviewerApiPaths } from "@/src/constants/codeReviewerConstants.js";
import { prStatuses } from "@/src/constants/codebaseConstants.js";

export default {
  base: {
    repos: {
      async create(data) {
        try {
          const res = await codebaseTransport.post(`/repos`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating repository: ", error);
          throw error;
        }
      },

      async list() {
        try {
          const res = await codebaseTransport.get(`/repos`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing repositories: ", error);
          throw error;
        }
      },

      async get(id) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching repository: ", error);
          throw error;
        }
      },

      async update(id, data) {
        try {
          const res = await codebaseTransport.patch(`/repos/${id}`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating repository: ", error);
          throw error;
        }
      },

      async delete(id) {
        try {
          const res = await codebaseTransport.delete(`/repos/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting repository: ", error);
          throw error;
        }
      },

      async stats(id) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/stats`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching repository stats: ", error);
          throw error;
        }
      },

      async fork(id, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/forks`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while forking repository: ", error);
          throw error;
        }
      },

      async listForks(id) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/forks`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing repository forks: ", error);
          throw error;
        }
      },
    },

    branches: {
      async list(id) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/branches`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing branches: ", error);
          throw error;
        }
      },

      async get(id, branch) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/branches/${branch}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching branch: ", error);
          throw error;
        }
      },

      async create(id, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/branches`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating branch: ", error);
          throw error;
        }
      },

      async delete(id, branch) {
        try {
          const res = await codebaseTransport.delete(`/repos/${id}/branches/${branch}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting branch: ", error);
          throw error;
        }
      },
    },

    commits: {
      async list(id, params) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/commits`, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing commits: ", error);
          throw error;
        }
      },

      async search(id, body) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/commits/search`, body);
          return res.data;
        } catch (error) {
          console.error("An error occurred while searching commits: ", error);
          throw error;
        }
      },

      async get(id, sha) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/commits/${sha}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching commit: ", error);
          throw error;
        }
      },

      async getTree(id, path, params) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/commits/tree/${path}`, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching tree: ", error);
          throw error;
        }
      },

      async getBlob(id, path, params) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/commits/blob/${path}`, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching blob: ", error);
          throw error;
        }
      },

      async getDiff(id, params) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/commits/diff`, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching diff: ", error);
          throw error;
        }
      },
    },

    pulls: {
      async create(id, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/pulls`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating pull request: ", error);
          throw error;
        }
      },

      async list(id, params) {
        try {
          const { page, limit, query, ...filters } = params || {};
          const queryParams = {};
          if (page) queryParams.page = page;
          if (limit) queryParams.limit = limit;
          if (query) queryParams.query = query;
          const res = await codebaseTransport.post(`/repos/${id}/pulls/search`, filters, { params: queryParams });
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing pull requests: ", error);
          throw error;
        }
      },

      async get(id, number) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/pulls/${number}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching pull request: ", error);
          throw error;
        }
      },

      async merge(id, number, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/pulls/${number}/merge`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while merging pull request: ", error);
          throw error;
        }
      },

      async revert(id, number) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/pulls/${number}/revert`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while reverting pull request: ", error);
          throw error;
        }
      },

      async addReview(id, number, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/pulls/${number}/reviews`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while adding pull request review: ", error);
          throw error;
        }
      },

      async updateParticipant(id, number, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/pulls/${number}/participants`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating PR participant: ", error);
          throw error;
        }
      },

      async listActivities(prId, filters = {}) {
        try {
          const params = { pr_id: prId };
          if (filters.event_types?.length) params.event_types = filters.event_types;
          if (filters.author_id) params.author_id = filters.author_id;
          if (filters.from_date) params.from_date = filters.from_date;
          const res = await transport.get(`/codebase_activities`, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing PR activities: ", error);
          throw error;
        }
      },

      async listComments(id, number) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/pulls/${number}/comments`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing pull request comments: ", error);
          throw error;
        }
      },

      async addComment(id, number, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/pulls/${number}/comments`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while adding pull request comment: ", error);
          throw error;
        }
      },

      async toggleComment(id, number, commentId) {
        try {
          const res = await codebaseTransport.put(`/repos/${id}/pulls/${number}/comments/${commentId}/resolve`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while toggling comment resolution: ", error);
          throw error;
        }
      },

      async listReviews(id, number) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/pulls/${number}/reviews`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing PR reviews: ", error);
          throw error;
        }
      },

      async close(id, number) {
        try {
          const res = await codebaseTransport.patch(`/repos/${id}/pulls/${number}`, { status: prStatuses.CLOSED });
          return res.data;
        } catch (error) {
          console.error("An error occurred while closing pull request: ", error);
          throw error;
        }
      },

      async reopen(id, number) {
        try {
          const res = await codebaseTransport.patch(`/repos/${id}/pulls/${number}`, { status: prStatuses.OPEN });
          return res.data;
        } catch (error) {
          console.error("An error occurred while reopening pull request: ", error);
          throw error;
        }
      },

      async convertToDraft(id, number) {
        try {
          const res = await codebaseTransport.patch(`/repos/${id}/pulls/${number}`, { status: prStatuses.DRAFT });
          return res.data;
        } catch (error) {
          console.error("An error occurred while converting PR to draft: ", error);
          throw error;
        }
      },

      async update(id, number, data) {
        try {
          const res = await codebaseTransport.patch(`/repos/${id}/pulls/${number}`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating pull request: ", error);
          throw error;
        }
      },

      async mergeability(id, number) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/pulls/${number}/mergeability`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching mergeability: ", error);
          throw error;
        }
      },

      async updateBranch(id, number, strategy = "merge") {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/pulls/${number}/update-branch`, { strategy });
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating branch: ", error);
          throw error;
        }
      },

      async getContributors(id, number) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/pulls/${number}/contributors`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching PR contributors: ", error);
          throw error;
        }
      },
    },

    issues: {
      async create(id, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/issues`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating issue: ", error);
          throw error;
        }
      },

      async list(id) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/issues`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing issues: ", error);
          throw error;
        }
      },

      async get(id, number) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/issues/${number}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching issue: ", error);
          throw error;
        }
      },

      async update(id, number, data) {
        try {
          const res = await codebaseTransport.patch(`/repos/${id}/issues/${number}`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating issue: ", error);
          throw error;
        }
      },

      async delete(id, number) {
        try {
          const res = await codebaseTransport.delete(`/repos/${id}/issues/${number}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting issue: ", error);
          throw error;
        }
      },
    },

    webhooks: {
      async create(id, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/webhooks`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating webhook: ", error);
          throw error;
        }
      },

      async list(id) {
        try {
          const res = await codebaseTransport.get(`/repos/${id}/webhooks`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing webhooks: ", error);
          throw error;
        }
      },

      async update(id, webhookID, data) {
        try {
          const res = await codebaseTransport.put(`/repos/${id}/webhooks/${webhookID}`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating webhook: ", error);
          throw error;
        }
      },

      async delete(id, webhookID) {
        try {
          const res = await codebaseTransport.delete(`/repos/${id}/webhooks/${webhookID}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting webhook: ", error);
          throw error;
        }
      },
    },

    branchProtection: {
      async list(repoId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/protection-rules`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing branch protection rules: ", error);
          throw error;
        }
      },

      async get(repoId, ruleId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/protection-rules/${ruleId}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching branch protection rule: ", error);
          throw error;
        }
      },

      async create(repoId, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${repoId}/protection-rules`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating branch protection rule: ", error);
          throw error;
        }
      },

      async update(repoId, ruleId, data) {
        try {
          const res = await codebaseTransport.put(`/repos/${repoId}/protection-rules/${ruleId}`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating branch protection rule: ", error);
          throw error;
        }
      },

      async delete(repoId, ruleId) {
        try {
          const res = await codebaseTransport.delete(`/repos/${repoId}/protection-rules/${ruleId}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting branch protection rule: ", error);
          throw error;
        }
      },

      async schema(repoId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/protection-rules/schema`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching branch protection rule schema: ", error);
          throw error;
        }
      },
    },

    rulesets: {
      async list(repoId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/rulesets`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing rulesets: ", error);
          throw error;
        }
      },

      async get(repoId, rulesetId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/rulesets/${rulesetId}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching ruleset: ", error);
          throw error;
        }
      },

      async create(repoId, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${repoId}/rulesets`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating ruleset: ", error);
          throw error;
        }
      },

      async update(repoId, rulesetId, data) {
        try {
          const res = await codebaseTransport.put(`/repos/${repoId}/rulesets/${rulesetId}`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating ruleset: ", error);
          throw error;
        }
      },

      async delete(repoId, rulesetId) {
        try {
          const res = await codebaseTransport.delete(`/repos/${repoId}/rulesets/${rulesetId}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting ruleset: ", error);
          throw error;
        }
      },

      async schema(repoId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/rulesets/schema`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching ruleset schema: ", error);
          throw error;
        }
      },
    },

    statuses: {
      async create(repoId, sha, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${repoId}/statuses/${sha}`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating commit status: ", error);
          throw error;
        }
      },

      async list(repoId, sha) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/statuses/${sha}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing commit statuses: ", error);
          throw error;
        }
      },
    },

    files: {
      async create(id, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/commits/files`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating file: ", error);
          throw error;
        }
      },

      async batchCreate(id, data) {
        try {
          const res = await codebaseTransport.post(`/repos/${id}/commits/files/batch`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while batch creating files: ", error);
          throw error;
        }
      },
    },

    tokens: {
      async list() {
        try {
          const res = await codebaseTransport.get(`/tokens`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing token: ", error);
          throw error;
        }
      },

      async create(payload) {
        try {
          const res = await codebaseTransport.post(`/tokens`, payload);
          return res.data;
        } catch (error) {
          console.error("An error occurred while creating token: ", error);
          throw error;
        }
      },

      async delete(id) {
        try {
          const res = await codebaseTransport.delete(`/tokens/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting token: ", error);
          throw error;
        }
      },
    },

    collaborators: {
      async myRole(repoId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/my-role`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching my role: ", error);
          throw error;
        }
      },

      async list(repoId) {
        try {
          const res = await codebaseTransport.get(`/repos/${repoId}/collaborators`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing collaborators: ", error);
          throw error;
        }
      },

      async grantUser(repoId, userId, role) {
        try {
          const res = await codebaseTransport.put(`/repos/${repoId}/collaborators/${userId}`, { role });
          return res.data;
        } catch (error) {
          console.error("An error occurred while granting user access: ", error);
          throw error;
        }
      },

      async revokeUser(repoId, userId) {
        try {
          const res = await codebaseTransport.delete(`/repos/${repoId}/collaborators/${userId}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while revoking user access: ", error);
          throw error;
        }
      },

      async grantTeam(repoId, teamId, role) {
        try {
          const res = await codebaseTransport.put(`/repos/${repoId}/teams/${teamId}`, { role });
          return res.data;
        } catch (error) {
          console.error("An error occurred while granting team access: ", error);
          throw error;
        }
      },

      async revokeTeam(repoId, teamId) {
        try {
          const res = await codebaseTransport.delete(`/repos/${repoId}/teams/${teamId}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while revoking team access: ", error);
          throw error;
        }
      },
    },

    sshKeys: {
      async list() {
        try {
          const res = await codebaseTransport.get(`/ssh-keys`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing SSH keys: ", error);
          throw error;
        }
      },

      async create(payload) {
        try {
          const res = await codebaseTransport.post(`/ssh-keys`, payload);
          return res.data;
        } catch (error) {
          console.error("An error occurred while adding SSH key: ", error);
          throw error;
        }
      },

      async delete(id) {
        try {
          const res = await codebaseTransport.delete(`/ssh-keys/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting SSH key: ", error);
          throw error;
        }
      },
    },
  },

  actions: {
    workflows: {
      async list(params) {
        try {
          const res = await codeActionsTransport.get("/v1/workflows", { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing workflows: ", error);
          throw error;
        }
      },

      async get(id) {
        try {
          const res = await codeActionsTransport.get(`/v1/workflows/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching workflow: ", error);
          throw error;
        }
      },

      async getFile(id) {
        try {
          const res = await codeActionsTransport.get(`/v1/workflows/${id}/file`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching workflow file: ", error);
          throw error;
        }
      },

      async dispatch(id, data) {
        try {
          const res = await codeActionsTransport.post(`/v1/workflows/${id}/dispatch`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while dispatching workflow: ", error);
          throw error;
        }
      },
    },

    runs: {
      async list(params) {
        try {
          const res = await codeActionsTransport.get("/v1/runs", { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing runs: ", error);
          throw error;
        }
      },

      async get(id) {
        try {
          const res = await codeActionsTransport.get(`/v1/runs/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching run: ", error);
          throw error;
        }
      },

      async getJobs(id) {
        try {
          const res = await codeActionsTransport.get(`/v1/runs/${id}/jobs`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching run jobs: ", error);
          throw error;
        }
      },

      async getUsage(id) {
        try {
          const res = await codeActionsTransport.get(`/v1/runs/${id}/usage`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching run usage: ", error);
          throw error;
        }
      },

      async getFile(id) {
        try {
          const res = await codeActionsTransport.get(`/v1/runs/${id}/file`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching run file: ", error);
          throw error;
        }
      },

      async cancel(id) {
        try {
          const res = await codeActionsTransport.post(`/v1/runs/${id}/cancel`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while cancelling run: ", error);
          throw error;
        }
      },

      async rerun(id) {
        try {
          const res = await codeActionsTransport.post(`/v1/runs/${id}/rerun`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while rerunning workflow: ", error);
          throw error;
        }
      },
    },

    jobs: {
      async getSteps(jobId) {
        try {
          const res = await codeActionsTransport.get(`/v1/jobs/${jobId}/steps`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching job steps: ", error);
          throw error;
        }
      },

      async getLogs(jobId) {
        try {
          const res = await codeActionsTransport.get(`/v1/jobs/${jobId}/logs`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching job logs: ", error);
          throw error;
        }
      },
    },

    steps: {
      async getLogs(stepId) {
        try {
          const res = await codeActionsTransport.get(`/v1/steps/${stepId}/logs`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching step logs: ", error);
          throw error;
        }
      },
    },

    templates: {
      async list(params) {
        try {
          const res = await codeActionsTransport.get("/v1/templates", { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while listing templates: ", error);
          throw error;
        }
      },

      async getCategories() {
        try {
          const res = await codeActionsTransport.get("/v1/templates/categories");
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching template categories: ", error);
          throw error;
        }
      },

      async get(id) {
        try {
          const res = await codeActionsTransport.get(`/v1/templates/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while fetching template: ", error);
          throw error;
        }
      },

      async apply(id, data) {
        try {
          const res = await codeActionsTransport.post(`/v1/templates/${id}/apply`, data);
          return res.data;
        } catch (error) {
          console.error("An error occurred while applying template: ", error);
          throw error;
        }
      },
    },
  },

  reviewer: {
    install: {
      async githubUrl() {
        try {
          const res = await codeReviewerTransport.get(reviewerApiPaths.INSTALL_GITHUB);
          return res.data;
        } catch (error) {
          console.error("An error occurred while getting GitHub install URL: ", error);
          throw error;
        }
      },
    },

    integrations: {
      async list() {
        try {
          const res = await codeReviewerTransport.get(reviewerApiPaths.INTEGRATIONS);
          return res.data;
        } catch (error) {
          console.error("An error occurred while getting integrations: ", error);
          throw error;
        }
      },

      async update(id, payload) {
        try {
          const res = await codeReviewerTransport.patch(`${reviewerApiPaths.INTEGRATIONS}/${id}`, payload);
          return res.data;
        } catch (error) {
          console.error("An error occurred while updating integration: ", error);
          throw error;
        }
      },

      async delete(id) {
        try {
          const res = await codeReviewerTransport.delete(`${reviewerApiPaths.INTEGRATIONS}/${id}`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while deleting integration: ", error);
          throw error;
        }
      },

      async setPrimary(id) {
        try {
          const res = await codeReviewerTransport.put(`${reviewerApiPaths.INTEGRATIONS}/${id}/primary`);
          return res.data;
        } catch (error) {
          console.error("An error occurred while setting primary integration: ", error);
          throw error;
        }
      },
    },

    organizations: {
      async list(params) {
        try {
          const res = await codeReviewerTransport.get(reviewerApiPaths.ORGANIZATIONS, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while getting organizations: ", error);
          throw error;
        }
      },
    },

    repositories: {
      async list(params) {
        try {
          const res = await codeReviewerTransport.get(reviewerApiPaths.CODE_REPOSITORIES, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while getting code repositories: ", error);
          throw error;
        }
      },
    },

    reviews: {
      async list(params) {
        try {
          const res = await codeReviewerTransport.get(reviewerApiPaths.REVIEWS, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while getting reviews: ", error);
          throw error;
        }
      },

      async stats(params) {
        try {
          const res = await codeReviewerTransport.get(reviewerApiPaths.REVIEW_STATS, { params });
          return res.data;
        } catch (error) {
          console.error("An error occurred while getting review stats: ", error);
          throw error;
        }
      },
    },
  },

  coder: {
    admin: {
      async migrateToCodebase(appId) {
        try {
          const payload = appId ? { app_id: appId } : {};
          const res = await coderTransport.post(
            `/v1/admin/migrate-to-codebase`,
            payload,
          );
          return res.data;
        } catch (error) {
          console.error(
            "An error occurred while migrating to codebase: ",
            error,
          );
          throw error;
        }
      },
    },
  },
};
